import { useEffect, useState, useCallback, useRef } from "react";
import { localId } from "./ulid";
import type { Message, FileEntry } from "./types";
import { apiGet, apiPost, apiPatch, apiDelete, resolveApiUrl } from "./api";
import { useSession } from "./auth";
import { uploadFile, fileDownloadUrl } from "./files";
import { useWebSocket, type WsMessage } from "./ws";
import { queryClient } from "./queryClient";
import { integrationAuthorId, parseIntegrationSender } from "./message-sender";

export interface SendOptions { attachments?: File[]; mentions?: string[]; parentId?: string; }
export interface ServerMessage {
  id: string; room_id: string; user_id: string | null; body_md: string; created_at: number;
  client_message_id?: string | null; deleted_at?: number | null; parent_id?: string | null;
  thread_root_id?: string | null;
  edited_at?: number | null; reply_count?: number; is_pinned?: boolean;
  type?: string;
  display_name?: string;
  sender?: { kind?: string; webhook_id?: string; display_name?: string } | null;
  import_source?: { provider: "slack"; author_id?: string; author_name?: string; timestamp?: string } | null;
  mentions?: { mention_type: string; target_id?: string | null }[];
  reactions?: { emoji: string; count: number; user_ids: string[] }[];
  attachments?: { id: string; filename?: string; mime_type?: string; size_bytes?: number }[];
}
interface Page { messages: ServerMessage[]; has_more?: boolean; next_cursor?: string | null; }
export function toClientMessage(m: ServerMessage, selfId?: string): Message {
  const sender = parseIntegrationSender(m.sender);
  return { id: m.id, localId: m.client_message_id || undefined, roomId: m.room_id,
    authorId: sender ? integrationAuthorId(sender.webhook_id) : (m.user_id ?? ""),
    authorName: sender?.display_name || m.display_name || (m.import_source?.provider === "slack" && m.import_source.author_name ? `${m.import_source.author_name} (Slack)` : undefined),
    sender,
    body: m.deleted_at ? "メッセージは削除されました" : m.body_md,
    createdAt: new Date(m.created_at).toISOString(), status: "sent",
    parentId: m.parent_id || undefined, threadRootId: m.thread_root_id || undefined, editedAt: m.edited_at ? new Date(m.edited_at).toISOString() : undefined,
    replyCount: m.reply_count, pinned: m.is_pinned,
    reactions: m.reactions?.map(r => ({ emoji: r.emoji, count: r.count, byMe: !!selfId && r.user_ids.includes(selfId) })),
    attachments: m.attachments?.map(f => ({ id: f.id, name: f.filename || f.id, mime: f.mime_type, size: f.size_bytes })) };
}
/** Merge persisted messages and optimistic sends by both server and idempotency IDs. */
export function mergeMessages(current: Message[], incoming: Message[]): Message[] {
  const merged = [...current];
  for (const message of incoming) {
    const index = merged.findIndex(m => m.id === message.id || (!!message.localId && m.localId === message.localId));
    if (index < 0) merged.push(message);
    else merged[index] = { ...merged[index], ...message };
  }
  return merged.sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id));
}

function messagesPath(roomId: string, extras?: { cursor?: string | null; threadRootId?: string }) {
  const params = new URLSearchParams();
  if (extras?.cursor) params.set("cursor", extras.cursor);
  if (extras?.threadRootId) params.set("thread_root_id", extras.threadRootId);
  const qs = params.toString();
  return `/v1/rooms/${encodeURIComponent(roomId)}/messages${qs ? `?${qs}` : ""}`;
}

export function useRoomMessages(roomId?: string, options?: { threadRootId?: string }) {
  const session = useSession();
  const threadRootId = options?.threadRootId;
  const [messages, setMessages] = useState<Message[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [typing, setTyping] = useState<Record<string, number>>({});
  const activeRoom = useRef(roomId); activeRoom.current = roomId;
  const activeThread = useRef(threadRootId); activeThread.current = threadRootId;
  const drafts = useRef(new Map<string, { body: string; opts: SendOptions; uploaded?: FileEntry[]; roomId: string }>());
  const inflight = useRef(new Set<string>());
  const lastTypingAt = useRef(0);
  const hasLoadedPage = useRef(false);

  const scopeLive = (room: string | undefined, thread?: string) => activeRoom.current === room && activeThread.current === thread;
  const enrich = useCallback(async (items: Message[], target: string, thread?: string) => {
    const attachments = items.flatMap(m => m.attachments || []).filter(f => f.id && !f.url);
    for (const file of attachments) {
      try {
        const url = await fileDownloadUrl(file.id!);
        if (!scopeLive(target, thread)) return;
        setMessages(prev => prev.map(m => ({ ...m, attachments: m.attachments?.map(f => f.id === file.id ? { ...f, url } : f) })));
      } catch { /* The message remains visible; inaccessible files keep their filename. */ }
    }
  }, []);
  const ingest = useCallback((records: ServerMessage[], target: string) => {
    if (!scopeLive(target, threadRootId)) return;
    const scoped = threadRootId
      ? records.filter(m => m.id === threadRootId || m.thread_root_id === threadRootId || m.parent_id === threadRootId)
      : records;
    if (!scoped.length) return;
    const items = scoped.map(m => toClientMessage(m, session?.user.id));
    setMessages(previous => mergeMessages(previous, items));
    void enrich(items, target, threadRootId);
  }, [session?.user.id, enrich, threadRootId]);
  const refresh = useCallback(async () => {
    if (!roomId || !session) return;
    try {
      const page = await apiGet<Page>(messagesPath(roomId, { threadRootId }));
      if (activeRoom.current !== roomId || activeThread.current !== threadRootId) return;
      ingest(page.messages, roomId);
      if (!hasLoadedPage.current) { setCursor(page.next_cursor ?? null); setHasMore(page.has_more ?? false); hasLoadedPage.current = true; }
      setError(null);
    } catch (e) { if (scopeLive(roomId, threadRootId)) setError(e instanceof Error ? e : new Error(String(e))); }
  }, [roomId, threadRootId, session?.user.id, ingest]);
  useEffect(() => {
    hasLoadedPage.current = false;
    setMessages([]); setTyping({}); setCursor(null); setHasMore(false); setError(null); setLoading(true); setLoadingEarlier(false);
    void refresh().finally(() => { if (activeRoom.current === roomId && activeThread.current === threadRootId) setLoading(false); });
  }, [roomId, threadRootId, refresh]);
  const loadEarlier = useCallback(async () => {
    if (!roomId || !cursor || !hasMore || loadingEarlier) return;
    setLoadingEarlier(true);
    try {
      const page = await apiGet<Page>(messagesPath(roomId, { cursor, threadRootId }));
      if (activeRoom.current !== roomId || activeThread.current !== threadRootId) return;
      ingest(page.messages, roomId); setCursor(page.next_cursor ?? null); setHasMore(page.has_more ?? false);
    } catch (e) { if (scopeLive(roomId, threadRootId)) setError(e instanceof Error ? e : new Error(String(e))); }
    finally { if (scopeLive(roomId, threadRootId)) setLoadingEarlier(false); }
  }, [roomId, threadRootId, cursor, hasMore, loadingEarlier, ingest]);
  const onEvent = useCallback((event: WsMessage) => {
    if (!roomId) return;
    const payload = event.payload as Record<string, unknown> | undefined;
    if (!payload) return;
    if (event.type === "message.created" && payload.message) {
      const record = payload.message as ServerMessage;
      ingest([record], roomId);
    }
    else if (event.type === "message.edited") setMessages(prev => prev.map(m => m.id === payload.message_id ? { ...m, body: String(payload.body_md), editedAt: new Date(Number(payload.edited_at)).toISOString() } : m));
    else if (event.type === "message.deleted") setMessages(prev => prev.filter(m => m.id !== payload.message_id));
    else if (event.type === "typing.started" && payload.user_id !== session?.user.id) setTyping(prev => ({ ...prev, [String(payload.user_id)]: Number(payload.expires_at) }));
    else if (event.type === "typing.stopped") setTyping(prev => { const next = { ...prev }; delete next[String(payload.user_id)]; return next; });
    else if (event.type.startsWith("reaction.") || event.type.startsWith("pin.")) { void refresh(); void queryClient.invalidateQueries({ queryKey: ["pins"] }); }
    else if (event.type.startsWith("room.member_")) {
      void queryClient.invalidateQueries({ queryKey: ["room-members", roomId] });
      void queryClient.invalidateQueries({ queryKey: ["rooms"] });
      if (event.type === "room.member_removed" && payload.user_id === session?.user.id) void refresh();
    }
    if (event.type.startsWith("message.") || event.type.startsWith("read.")) void queryClient.invalidateQueries({ queryKey: ["rooms"] });
  }, [roomId, threadRootId, session?.user.id, ingest, refresh]);
  const wsUrl = new URL(resolveApiUrl(`/v1/rooms/${encodeURIComponent(roomId ?? "")}/ws`), window.location.origin);
  wsUrl.protocol = wsUrl.protocol === "https:" ? "wss:" : "ws:";
  const ws = useWebSocket(wsUrl.toString(), { token: session?.token, enabled: !!roomId && !!session, onMessage: onEvent, onOpen: refresh });
  useEffect(() => {
    const interval = setInterval(() => setTyping(prev => Object.fromEntries(Object.entries(prev).filter(([, end]) => end > Date.now()))), 1000);
    return () => clearInterval(interval);
  }, []);

  const submit = useCallback(async (id: string) => {
    const draft = drafts.current.get(id);
    if (!draft || inflight.current.has(id)) return;
    inflight.current.add(id);
    try {
      if (!draft.uploaded) draft.uploaded = [];
      for (const file of (draft.opts.attachments || []).slice(draft.uploaded.length)) draft.uploaded.push(await uploadFile(file, draft.roomId));
      const result = await apiPost<{ message: ServerMessage }>(`/v1/rooms/${encodeURIComponent(draft.roomId)}/messages`, {
        body_md: draft.body, client_message_id: id, parent_id: draft.opts.parentId,
        type: draft.opts.parentId ? "reply" : draft.uploaded.length ? "file" : "text",
        attachments: draft.uploaded.map(f => ({ id: f.id, kind: f.mime.startsWith("image/") ? "image" : "file", filename: f.name, mime_type: f.mime, size_bytes: f.size })),
        mentions: draft.opts.mentions?.map(target => ({ mention_type: target === "here" || target === "channel" ? target : "user", target_id: target === "here" || target === "channel" ? null : target })),
      });
      if (scopeLive(draft.roomId, threadRootId)) ingest([result.message], draft.roomId);
      drafts.current.delete(id);
    } catch (e) {
      if (scopeLive(draft.roomId, threadRootId)) { setMessages(prev => prev.map(m => m.localId === id ? { ...m, status: "failed" } : m)); setError(e instanceof Error ? e : new Error(String(e))); }
    } finally { inflight.current.delete(id); }
  }, [ingest, threadRootId]);
  const send = useCallback((body: string, opts: SendOptions = {}) => {
    const id = localId();
    if (!roomId || !session || (!body.trim() && !opts.attachments?.length)) return id;
    const sendOpts = { ...opts, parentId: opts.parentId ?? threadRootId };
    drafts.current.set(id, { body: body.trim(), opts: sendOpts, roomId });
    setMessages(prev => [...prev, { id, localId: id, roomId, authorId: session.user.id, authorName: session.user.displayName,
      body: body.trim(), createdAt: new Date().toISOString(), status: "sending", parentId: sendOpts.parentId, threadRootId: threadRootId || sendOpts.parentId }]);
    void submit(id); return id;
  }, [roomId, threadRootId, session, submit]);
  const retry = useCallback((id: string) => { setMessages(prev => prev.map(m => m.localId === id ? { ...m, status: "sending" } : m)); void submit(id); }, [submit]);
  const remove = useCallback(async (id: string) => { await apiDelete(`/v1/messages/${encodeURIComponent(id)}`); setMessages(prev => prev.filter(m => m.id !== id)); }, []);
  const edit = useCallback(async (id: string, body: string) => { const result = await apiPatch<{ message: ServerMessage }>(`/v1/messages/${encodeURIComponent(id)}`, { body_md: body }); if (roomId) ingest([result.message], roomId); }, [roomId, ingest]);
  const react = useCallback(async (id: string, emoji = "👍") => {
    const existing = messages.find(m => m.id === id)?.reactions?.find(r => r.emoji === emoji && r.byMe);
    const path = `/v1/messages/${encodeURIComponent(id)}/reactions`;
    const result = existing ? await apiDelete<{ message: ServerMessage }>(`${path}/${encodeURIComponent(emoji)}`) : await apiPost<{ message: ServerMessage }>(path, { emoji });
    if (roomId) ingest([result.message], roomId);
  }, [messages, roomId, ingest]);
  const pin = useCallback(async (id: string) => { const path = `/v1/messages/${encodeURIComponent(id)}/pin`; if (messages.find(m => m.id === id)?.pinned) await apiDelete(path); else await apiPost(path, {}); await refresh(); await queryClient.invalidateQueries({ queryKey: ["pins"] }); }, [messages, refresh]);
  const loadMessage = useCallback(async (id: string) => {
    const result = await apiGet<{ message: ServerMessage }>(`/v1/messages/${encodeURIComponent(id)}`);
    if (!scopeLive(roomId, threadRootId) || !roomId || result.message.room_id !== roomId) return result.message;
    ingest([result.message], roomId);
    return result.message;
  }, [roomId, threadRootId, ingest]);
  return { loadMessage, messages, send, retry, remove, edit, react, pin, refresh, error, loading, loadEarlier,
    loadingEarlier, reachedStart: !hasMore, connection: ws.state, typingUserIds: Object.keys(typing),
    sendTyping: () => { if (Date.now() - lastTypingAt.current < 2000) return false; lastTypingAt.current = Date.now(); return ws.send({ type: "typing", payload: { room_id: roomId } }); } };
}
